import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@workspace/ui/components/Button';
import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import { useGlobal } from '~/hooks/useGlobal';
import { userStore } from '~/stores';
import type { ReviewMedia } from '~/types/Product/interface';

import ReviewCard from './components/ReviewCard';
import ReviewMediaLightbox from './components/ReviewMediaLightbox';
import type { MediaWithReview, ProductReviewsProps } from './interfaces';
import styles from './ProductReviews.module.scss';

const ProductReviews = observer(
  ({
    className,
    reviews = [],
    totalCount = 0,
    onLoadMore,
    canLoadMore,
    canCreateReview = false,
    onCreateReview,
  }: ProductReviewsProps) => {
    const { t } = useTranslation();
    const { isMobile } = useGlobal();

    const [lightboxState, setLightboxState] = useState<{
      isOpen: boolean;
      initialIndex: number;
    }>({
      isOpen: false,
      initialIndex: 0,
    });

    const allMediaWithReviews = useMemo<MediaWithReview[]>(() => {
      const result: MediaWithReview[] = [];
      for (const review of reviews) {
        if (review.media) {
          for (const media of review.media) {
            result.push({ media, review });
          }
        }
      }
      return result;
    }, [reviews]);

    const handleMediaClick = useCallback(
      (mediaIndex: number, reviewMedia: ReviewMedia[]) => {
        let globalIndex = 0;
        let targetGlobalIndex = 0;

        for (const review of reviews) {
          if (review.media) {
            for (let i = 0; i < review.media.length; i++) {
              if (review.media === reviewMedia && i === mediaIndex) {
                targetGlobalIndex = globalIndex;
              }
              globalIndex++;
            }
          }
        }

        setLightboxState({
          isOpen: true,
          initialIndex: targetGlobalIndex,
        });
      },
      [reviews],
    );

    const handleCloseLightbox = useCallback(() => {
      setLightboxState((prev) => ({ ...prev, isOpen: false }));
    }, []);

    const showAddReviewButton = userStore.isAuthorized && canCreateReview;

    if (!reviews.length && !showAddReviewButton) return null;

    const ProductReviewsComponent = isMobile
      ? Typography.Header100Semibold
      : company.match({
          mcosmetic: Typography.Header400Semibold,
          default: Typography.Header400Medium,
        });
    const TotalCountComponent = isMobile
      ? Typography.Header100Medium
      : Typography.Header400Medium;

    const buttonSize = isMobile ? 'lg' : 'xl';

    return (
      <section className={cn(styles.root, className)}>
        <div className={styles.header}>
          <ProductReviewsComponent as="h2" className={styles.title}>
            {t('productReviews')}
          </ProductReviewsComponent>
          <TotalCountComponent className={styles.count}>
            {totalCount}
          </TotalCountComponent>
        </div>

        {reviews.length > 0 && (
          <div className={styles.reviewsList}>
            {reviews.map((review, index) => (
              <ReviewCard
                key={review.id}
                review={review}
                showDivider={index < reviews.length - 1}
                onMediaClick={handleMediaClick}
              />
            ))}
          </div>
        )}

        {showAddReviewButton && (
          <Button
            variant="brand"
            size={buttonSize}
            className="w-full md:w-max"
            onClick={onCreateReview}
          >
            {t('addReview')}
          </Button>
        )}

        {canLoadMore ? (
          <Button
            variant="secondary"
            size={buttonSize}
            className={styles.viewAllButton}
            onClick={onLoadMore}
          >
            {t('showMore')}
          </Button>
        ) : null}

        <ReviewMediaLightbox
          isOpen={lightboxState.isOpen}
          onClose={handleCloseLightbox}
          allMediaWithReviews={allMediaWithReviews}
          initialIndex={lightboxState.initialIndex}
        />
      </section>
    );
  },
);

export default ProductReviews;
